/**
 * In-memory cache for conversation details to avoid refetching
 * Provides instant load times for revisited conversations
 */

interface CachedConversation {
  data: any;
  timestamp: number;
}

class ConversationCache {
  private cache = new Map<number, CachedConversation>();
  private readonly TTL = 2 * 60 * 1000; // 2 minutes cache duration
  
  /**
   * Get cached conversation if available and not expired
   */
  get(conversationId: number): any | null {
    const cached = this.cache.get(conversationId);
    if (!cached) {
      return null;
    }
    
    // Check if expired
    const age = Date.now() - cached.timestamp;
    if (age > this.TTL) {
      this.cache.delete(conversationId);
      console.debug(`🗑️ Cache expired for conversation ${conversationId} (age: ${age}ms)`);
      return null;
    }
    
    console.log(`💾 Cache HIT for conversation ${conversationId} (age: ${age}ms)`);
    return cached.data;
  }
  
  /**
   * Store conversation in cache
   */
  set(conversationId: number, data: any): void {
    this.cache.set(conversationId, {
      data,
      timestamp: Date.now()
    });
    console.log(`💾 Cached conversation ${conversationId}`);
  }
  
  /**
   * Invalidate a specific conversation (e.g., after sending a message)
   */
  invalidate(conversationId: number): void {
    if (this.cache.delete(conversationId)) {
      console.log(`🗑️ Invalidated cache for conversation ${conversationId}`);
    }
  }
  
  /**
   * Clear entire cache
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`🗑️ Cleared conversation cache (${size} items)`);
  }
  
  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: number[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export const conversationCache = new ConversationCache();

